import React, { useEffect, useState } from "react";
import {
  ChevronDownIcon,
  RotateCw,
  Download,
  BarChart2,
  PieChart,
  ArrowLeft,
} from "lucide-react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import SchedulerRunHistoryDashboard from "./SchedulerRunHistoryDashboard";
import {
  getVAUsageSummary,
  getVAUsageByHat,
  getVAUsageChartData,
  getVAUsageExportUrl,
} from "../../../service/apiService";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface VAUsageSummaryItem {
  hat_id: string;
  hat_name: string;
  department: string;
  total_uses: number;
  unique_users: number;
  last_used: string;
}

interface VAUserUsageItem {
  user_id: string;
  total_uses: number;
  last_used: string;
}

interface PaginationData {
  page: number;
  items_per_page: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  next_page: number | null;
}

interface UsageMetadata {
  total_vas_used: number;
  total_uses: number;
  month: string;
}

interface ChartDataResponse {
  success: boolean;
  data: {
    months: string[];
    values: number[];
    label: string;
    metric_type: string;
  };
  statistics: {
    total: number;
    average: number;
    maximum: number;
    minimum: number;
    period: string;
  };
  chart_config: {
    chart_type: string;
    color: string;
    background_color: string;
  };
  unique_users_data: {
    months: string[];
    values: number[];
    label: string;
    metric_type: string;
  };
}

const VAUsageDashboard: React.FC = () => {
  const [summaryData, setSummaryData] = useState<VAUsageSummaryItem[]>([]);
  const [metadata, setMetadata] = useState<UsageMetadata | null>(null);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    items_per_page: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    next_page: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "chart">("table");

  // Detail view state
  const [selectedHat, setSelectedHat] = useState<{
    hat_id: string;
    hat_name: string;
  } | null>(null);
  const [detailData, setDetailData] = useState<VAUserUsageItem[]>([]);
  const [detailPagination, setDetailPagination] = useState<PaginationData>({
    page: 1,
    items_per_page: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    next_page: null,
  });
  const [detailLoading, setDetailLoading] = useState(false);

  // Chart state
  const [chartData, setChartData] = useState<ChartDataResponse | null>(null);
  const [chartLoading, setChartLoading] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
  });

  const fetchSummary = async (page: number, itemsPerPage: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getVAUsageSummary(page, itemsPerPage, selectedMonth);
      setSummaryData(result.data || []);
      setPagination(result.pagination);
      setMetadata(result.metadata);
    } catch (err) {
      console.error("Error fetching VA usage summary:", err);
      setError("Failed to fetch VA usage data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDetailData = async (
    hatId: string,
    page: number,
    itemsPerPage: number
  ) => {
    setDetailLoading(true);
    try {
      const result = await getVAUsageByHat(
        hatId,
        page,
        itemsPerPage,
        selectedMonth
      );
      setDetailData(result.data || []);
      setDetailPagination(result.pagination);
    } catch (err) {
      console.error("Error fetching VA usage detail:", err);
      setError("Failed to fetch VA detail data.");
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchChartData = async () => {
    setChartLoading(true);
    try {
      const result = await getVAUsageChartData(selectedMonth);
      setChartData(result);
    } catch (err) {
      console.error("Error fetching VA usage chart data:", err);
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary(pagination.page, pagination.items_per_page);
  }, [pagination.page, pagination.items_per_page, selectedMonth]);

  useEffect(() => {
    if (viewMode === "chart") {
      fetchChartData();
    }
  }, [viewMode, selectedMonth]);

  const handleMonthSubmit = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (selectedHat) {
      fetchDetailData(selectedHat.hat_id, 1, detailPagination.items_per_page);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowClick = (item: VAUsageSummaryItem) => {
    setSelectedHat({ hat_id: item.hat_id, hat_name: item.hat_name });
    setDetailPagination((prev) => ({ ...prev, page: 1 }));
    fetchDetailData(item.hat_id, 1, 10);
  };

  const handleBackToSummary = () => {
    setSelectedHat(null);
    setDetailData([]);
  };

  const handleDetailPageChange = (newPage: number) => {
    if (selectedHat) {
      setDetailPagination((prev) => ({ ...prev, page: newPage }));
      fetchDetailData(
        selectedHat.hat_id,
        newPage,
        detailPagination.items_per_page
      );
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await getVAUsageExportUrl(selectedMonth);
      const link = document.createElement("a");
      link.href = response;
      link.download = `${selectedMonth}-va-usage.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error exporting VA usage:", err);
      setError("Failed to export VA usage data. Please try again later.");
    } finally {
      setIsExporting(false);
    }
  };

  const formatNumber = (num: number) => num.toLocaleString();

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr.replace(" UTC", "Z"));
      return date.toLocaleDateString("default", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const getFormattedMonthDisplay = (yearMonthString: string) => {
    const [year, month] = yearMonthString.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, 1));
    return date.toLocaleDateString("default", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const },
      title: {
        display: true,
        text: `VA Usage (6 Months ending ${getFormattedMonthDisplay(selectedMonth)})`,
        font: { size: 14, weight: "bold" as const },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value: any) {
            return typeof value === "number" ? value.toLocaleString() : value;
          },
        },
      },
    },
  };

  return (
    <div>

      {/* Controls */}
      <div className="mb-4 flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4">
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="w-full md:w-auto rounded-md border border-gray-300 py-2 px-4 text-sm font-medium text-gray-900 bg-white hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 transition-colors duration-200"
        />
        <button
          onClick={handleMonthSubmit}
          className="w-full md:w-auto inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
        >
          Submit
        </button>
        <button
          onClick={handleExport}
          disabled={isExporting}
          className="w-full md:w-auto inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:bg-violet-400 disabled:cursor-not-allowed"
        >
          {isExporting ? (
            <>
              <RotateCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="-ml-1 mr-2 h-4 w-4" />
              Export
            </>
          )}
        </button>
        <div className="w-full md:w-auto md:ml-auto flex rounded-md shadow-sm">
          <button
            type="button"
            onClick={() => setViewMode("table")}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-l-md border ${
              viewMode === "table"
                ? "bg-violet-600 text-white border-violet-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            <BarChart2 className="-ml-1 mr-2 h-4 w-4" />
            Table
          </button>
          <button
            type="button"
            onClick={() => setViewMode("chart")}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-r-md border ${
              viewMode === "chart"
                ? "bg-violet-600 text-white border-violet-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            <PieChart className="-ml-1 mr-2 h-4 w-4" />
            Charts
          </button>
        </div>
      </div>

      {/* Empty State */}
      {!isLoading && !error && metadata && metadata.total_uses === 0 && summaryData.length === 0 && (
        <div className="mb-6 flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
          <BarChart2 className="w-10 h-10 text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm font-medium">No usage data recorded yet</p>
          <p className="text-gray-400 text-xs mt-1">
            VA usage will appear here once users start interacting with Virtual Assistants.
          </p>
        </div>
      )}

      {/* Summary Stats */}
      {metadata && metadata.total_uses > 0 && (
        <div className="mb-6 bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-violet-600" />
                <h3 className="text-base font-medium text-gray-900">
                  Usage Summary
                </h3>
              </div>
              <div className="text-sm text-gray-600">
                {getFormattedMonthDisplay(selectedMonth)}
              </div>
            </div>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-violet-50">
                <p className="text-sm text-violet-600 font-medium">
                  Total VA Uses
                </p>
                <p className="text-xl font-bold text-violet-700">
                  {formatNumber(metadata.total_uses)}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-violet-50">
                <p className="text-sm text-violet-600 font-medium">
                  Active Virtual Assistants
                </p>
                <p className="text-xl font-bold text-violet-700">
                  {formatNumber(metadata.total_vas_used)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chart View */}
      {viewMode === "chart" ? (
        <div className="space-y-6">
          {chartLoading ? (
            <div className="flex items-center justify-center h-64">
              <RotateCw className="animate-spin h-5 w-5 text-violet-500 mr-2" />
              <span className="text-violet-500">Loading chart data...</span>
            </div>
          ) : chartData && chartData.success ? (
            <div className="grid grid-cols-1 gap-6">
              {/* Total Uses Chart */}
              <div className="bg-white rounded-lg shadow p-4">
                <div className="chart-wrapper h-64">
                  <Bar
                    options={chartOptions}
                    data={{
                      labels: chartData.data.months.map((month) => {
                        const [year, monthNum] = month.split("-");
                        const date = new Date(
                          parseInt(year),
                          parseInt(monthNum) - 1,
                          1
                        );
                        return date.toLocaleDateString("default", {
                          month: "short",
                          year: "numeric",
                        });
                      }),
                      datasets: [
                        {
                          label: chartData.data.label,
                          data: chartData.data.values,
                          borderColor:
                            chartData.chart_config.color || "#7c3aed",
                          backgroundColor:
                            chartData.chart_config.background_color ||
                            "rgba(124, 58, 237, 0.6)",
                          borderWidth: 1,
                        },
                      ],
                    }}
                  />
                </div>
                <div className="flex justify-around mt-4 pt-4 border-t border-gray-100">
                  <div className="text-center">
                    <span className="block text-sm text-violet-600 font-medium">
                      Total
                    </span>
                    <span className="text-lg font-bold text-violet-800">
                      {chartData.statistics.total.toLocaleString()}
                    </span>
                  </div>
                  <div className="text-center">
                    <span className="block text-sm text-violet-600 font-medium">
                      Average/Month
                    </span>
                    <span className="text-lg font-bold text-violet-800">
                      {chartData.statistics.average.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Unique Users Chart */}
              {chartData.unique_users_data && (
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="chart-wrapper h-64">
                    <Bar
                      options={{
                        ...chartOptions,
                        plugins: {
                          ...chartOptions.plugins,
                          title: {
                            ...chartOptions.plugins.title,
                            text: `Unique Users (6 Months ending ${getFormattedMonthDisplay(selectedMonth)})`,
                          },
                        },
                      }}
                      data={{
                        labels: chartData.unique_users_data.months.map(
                          (month) => {
                            const [year, monthNum] = month.split("-");
                            const date = new Date(
                              parseInt(year),
                              parseInt(monthNum) - 1,
                              1
                            );
                            return date.toLocaleDateString("default", {
                              month: "short",
                              year: "numeric",
                            });
                          }
                        ),
                        datasets: [
                          {
                            label: chartData.unique_users_data.label,
                            data: chartData.unique_users_data.values,
                            borderColor: "#10B981",
                            backgroundColor: "rgba(16, 185, 129, 0.6)",
                            borderWidth: 1,
                          },
                        ],
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg p-4">
              <p className="text-gray-500">No chart data available</p>
            </div>
          )}
        </div>
      ) : (
        /* Table View */
        <>
          {selectedHat ? (
            /* Detail View - Users of a specific VA */
            <div>
              <button
                onClick={handleBackToSummary}
                className="mb-4 inline-flex items-center text-sm text-violet-600 hover:text-violet-800"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Summary
              </button>
              <h4 className="text-base font-medium text-gray-900 mb-4">
                {selectedHat.hat_name} — Users
              </h4>

              {/* Detail Pagination */}
              {detailPagination.total_pages > 1 && (
                <div className="mb-4 flex justify-between items-center">
                  <button
                    onClick={() =>
                      handleDetailPageChange(detailPagination.page - 1)
                    }
                    disabled={detailPagination.page === 1}
                    className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                      detailPagination.page === 1
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-violet-600 hover:bg-violet-700"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {detailPagination.page} of{" "}
                    {detailPagination.total_pages}
                  </span>
                  <button
                    onClick={() =>
                      handleDetailPageChange(detailPagination.page + 1)
                    }
                    disabled={!detailPagination.has_next}
                    className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                      !detailPagination.has_next
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-violet-600 hover:bg-violet-700"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
                  >
                    Next
                  </button>
                </div>
              )}

              <div className="overflow-x-auto rounded-lg">
                {detailLoading ? (
                  <div className="flex items-center space-x-2 py-8 justify-center">
                    <RotateCw className="animate-spin h-5 w-5 text-gray-500" />
                    <span>Loading...</span>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Uses
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Used
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {detailData.length > 0 ? (
                        detailData.map((user) => (
                          <tr key={user.user_id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.user_id}
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                                {formatNumber(user.total_uses)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                              {formatDate(user.last_used)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-6 py-4 text-center text-sm text-gray-500"
                          >
                            No usage data for this Virtual Assistant.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            /* Summary Table */
            <>
              {pagination.total_pages > 1 && (
                <div className="mb-4 flex justify-between items-center">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                      pagination.page === 1
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-violet-600 hover:bg-violet-700"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.has_next}
                    className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                      !pagination.has_next
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-violet-600 hover:bg-violet-700"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
                  >
                    Next
                  </button>
                </div>
              )}

              <div className="overflow-x-auto rounded-lg">
                {isLoading ? (
                  <div className="flex items-center space-x-2 py-8 justify-center">
                    <RotateCw className="animate-spin h-5 w-5 text-gray-500" />
                    <span>Loading...</span>
                  </div>
                ) : error ? (
                  <div className="text-red-500 py-4">{error}</div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Virtual Assistant
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Department
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Uses
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unique Users
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Used
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {summaryData.length > 0 ? (
                        summaryData.map((item) => (
                          <tr
                            key={item.hat_id}
                            onClick={() => handleRowClick(item)}
                            className="cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-violet-700">
                              {item.hat_name}
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                              {item.department || "-"}
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                                {formatNumber(item.total_uses)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                                {formatNumber(item.unique_users)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-500">
                              {formatDate(item.last_used)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-6 py-4 text-center text-sm text-gray-500"
                          >
                            No VA usage data available for this month.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
              {/* Inline Scheduler Run History — same Disclosure pattern
                  as Users List / Create User in UserManagement.tsx, so
                  admins get a consistent expand/collapse affordance.
                  Rendered independently of summaryData so the run history
                  is reachable even when there's no VA chat-usage for the
                  selected month. */}
              <Disclosure as="div" className="mt-4 py-2 border-t border-gray-200">
                <DisclosureButton className="group flex w-full items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                    Scheduler Run History
                  </span>
                  <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
                </DisclosureButton>
                <DisclosurePanel className="mt-4 text-sm text-gray-600">
                  <SchedulerRunHistoryDashboard />
                </DisclosurePanel>
              </Disclosure>
              {summaryData.length > 0 && (
                <p className="mt-2 text-xs text-gray-400">
                  Click a row to see per-user breakdown
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default VAUsageDashboard;
